import React, { useState, useEffect } from 'react';
import './App.css';
import MainEditor from './components/MainEditor';
import Header from './components/Header';
import Sidebar from './components/Sidebar';
import Frontpage from './pages/Frontpage';
import Login from './pages/Login';
import SettingsModal from './components/SettingsModal';
// Footer removed from global App render so editor views remain full-height

interface AppSettings {
  language: string;
  notifications: boolean;
  autosave: boolean;
  performanceMode: 'speed' | 'accuracy';
  fontSize: number;
  contrast: 'standard' | 'high';
  storage: 'local' | 'sync';
  schoolMode: boolean;
  schoolModePin?: string;
}

const DEFAULT_SETTINGS: AppSettings = {
  language: 'en-US',
  notifications: true,
  autosave: true,
  performanceMode: 'accuracy',
  fontSize: 14,
  contrast: 'standard',
  storage: 'local',
  schoolMode: false
};

const App: React.FC = () => {
  const [selectedLanguage, setSelectedLanguage] = useState('javascript');
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const [code, setCode] = useState('');

  const [showLogin, setShowLogin] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [settings, setSettings] = useState<AppSettings>(() => {
    try {
      const saved = localStorage.getItem('codenest_settings');
      return saved ? JSON.parse(saved) : DEFAULT_SETTINGS;
    } catch { return DEFAULT_SETTINGS; }
  });
  const [user, setUser] = useState<string | null>(() => {
    try {
      return localStorage.getItem('codenest_user');
    } catch { return null; }
  });

  const [bio, setBio] = useState<string>(() => {
    try {
      return localStorage.getItem('codenest_bio') || 'Code Explorer & Prompt Alchemist';
    } catch { return 'Code Explorer & Prompt Alchemist'; }
  });

  useEffect(() => {
    try {
      localStorage.setItem('codenest_bio', bio);
    } catch { }
  }, [bio]);

  useEffect(() => {
    const checkIfMobile = () => {
      setIsMobile(window.innerWidth <= 768);
      if (window.innerWidth > 768) setMobileMenuOpen(false);
    };
    checkIfMobile();
    window.addEventListener('resize', checkIfMobile);
    return () => window.removeEventListener('resize', checkIfMobile);
  }, []);

  // Initial load of code for the default language
  useEffect(() => {
    try {
      const saved = localStorage.getItem(`codenest_code_${selectedLanguage}`);
      if (saved) {
        setCode(saved);
      } else {
        setCode(''); // Reset or set default if needed
      }
    } catch (e) {
      console.error('Failed to load code from localStorage', e);
    }
  }, []); // Only on mount

  // Load code when language changes
  const [isInitialMount, setIsInitialMount] = useState(true);

  useEffect(() => {
    if (isInitialMount) {
      setIsInitialMount(false);
      return;
    }

    try {
      const saved = localStorage.getItem(`codenest_code_${selectedLanguage}`);
      setCode(saved || '');
    } catch (e) {
      console.error('Failed to load language-specific code', e);
    }
  }, [selectedLanguage]);

  // Save code when it changes (debounced or simple)
  useEffect(() => {
    if (isInitialMount) return;

    const handler = setTimeout(() => {
      try {
        localStorage.setItem(`codenest_code_${selectedLanguage}`, code || '');
      } catch (e) {
        console.error('Failed to save code to localStorage', e);
      }
    }, 500); // 500ms debounce

    return () => clearTimeout(handler);
  }, [code, selectedLanguage]);

  const handleMenuClick = () => setMobileMenuOpen(!mobileMenuOpen);

  const handleLanguageSelect = (language: string) => {
    setSelectedLanguage(language);
    if (isMobile) setMobileMenuOpen(false);
  };

  const handleLogin = (username: string) => {
    setUser(username);
    try {
      localStorage.setItem('codenest_user', username);
    } catch { }
  };

  const handleLogout = () => {
    setUser(null);
    try {
      localStorage.removeItem('codenest_user');
    } catch { }
  };

  if (!user) {
    return (
      <div className="app">
        {showLogin ? (
          <Login onLogin={handleLogin} onBack={() => setShowLogin(false)} />
        ) : (
          <Frontpage onGetStarted={() => setShowLogin(true)} onLogin={() => setShowLogin(true)} />
        )}
        <SettingsModal
          isOpen={showSettings}
          onClose={() => setShowSettings(false)}
          settings={settings}
          onSave={(newSettings: AppSettings) => {
            setSettings(newSettings);
            localStorage.setItem('codenest_settings', JSON.stringify(newSettings));
          }}
        />
      </div>
    );
  }

  return (
    <div className="app">
      <Header
        onMenuClick={handleMenuClick}
        onLogout={handleLogout}
        bio={bio}
        setBio={setBio}
        onOpenSettings={() => setShowSettings(true)}
        isMobile={isMobile}
        selectedLanguage={selectedLanguage}
        onLanguageSelect={handleLanguageSelect}
      />
      {mobileMenuOpen && isMobile && (
        <div className="sidebar-overlay" onClick={() => setMobileMenuOpen(false)} />
      )}
      <div className="app-content">
        <Sidebar
          collapsed={sidebarCollapsed}
          mobileOpen={mobileMenuOpen}
          onToggleCollapse={() => setSidebarCollapsed(!sidebarCollapsed)}
          selectedLanguage={selectedLanguage}
          onLanguageSelect={handleLanguageSelect}
        />
        <MainEditor
          language={selectedLanguage}
          sidebarCollapsed={sidebarCollapsed}
          code={code}
          setCode={setCode}
          settings={settings}
          isMobile={isMobile}
        />
      </div>
      <SettingsModal
        isOpen={showSettings}
        onClose={() => setShowSettings(false)}
        settings={settings}
        onSave={(newSettings: AppSettings) => {
          setSettings(newSettings);
          localStorage.setItem('codenest_settings', JSON.stringify(newSettings));
        }}
      />
      {/* Footer intentionally rendered only on the Frontpage to keep the editor full-height */}
    </div>
  );
};

export default App;
